/**
 * 带超时和 AbortController 的 fetch 封装
 */

/**
 * 创建带超时和取消控制的 fetch 请求
 * @param {string} url - 请求 URL
 * @param {object} options - fetch 选项
 * @param {number} timeout - 超时时间（毫秒）
 * @param {Array} controllerRegistry - 控制器注册表（用于批量取消）
 * @param {object} meta - 元数据
 * @returns {Promise<object>} 响应数据
 */
export async function fetchJson(url, options, timeout, controllerRegistry = null, meta = null) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const controllerRecord = controller ? {
    controller,
    url,
    startedAt: Date.now(),
    abortReason: null,
    finished: false,
    meta: meta || {}
  } : null;

  const requestOptions = Object.assign({}, options || {});
  if (controller) {
    requestOptions.signal = controller.signal;
    if (Array.isArray(controllerRegistry)) {
      controllerRegistry.push(controllerRecord);
    }
  }

  let timer = null;

  try {
    const response = await new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        if (controllerRecord) {
          controllerRecord.abortReason = 'timeout';
        }
        if (controller) {
          controller.abort();
        }
      }, timeout || 30000);

      fetch(url, requestOptions)
        .then(resolve)
        .catch(err => {
          if (err && err.name === 'AbortError') {
            const abortError = new Error(controllerRecord && controllerRecord.abortReason ? controllerRecord.abortReason : 'aborted');
            abortError.code = controllerRecord && controllerRecord.abortReason ? controllerRecord.abortReason : 'aborted';
            reject(abortError);
            return;
          }
          reject(err);
        });
    });

    const data = await response.json();

    if (!response.ok) {
      const requestError = new Error(data && data.message ? data.message : 'request_failed');
      requestError.code = data && data.error ? data.error : 'request_failed';
      requestError.payload = data;
      throw requestError;
    }

    return data;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
    if (controllerRecord) {
      controllerRecord.finished = true;
      if (Array.isArray(controllerRegistry)) {
        const idx = controllerRegistry.indexOf(controllerRecord);
        if (idx !== -1) {
          controllerRegistry.splice(idx, 1);
        }
      }
    }
  }
}

/**
 * 批量取消请求
 * @param {Array} controllerRegistry - 控制器注册表
 * @param {string} reason - 取消原因
 */
export function abortControllers(controllerRegistry, reason = 'aborted') {
  if (!Array.isArray(controllerRegistry) || !controllerRegistry.length) {
    return;
  }

  controllerRegistry.forEach(record => {
    if (!record || !record.controller || record.finished) {
      return;
    }

    record.abortReason = reason;
    try {
      record.controller.abort();
    } catch (err) {}
  });

  controllerRegistry.length = 0;
}
